//@ts-check
/**
 * The active locale, and the bound `t()` / formatters built from it.
 *
 * Why this is reactive rather than read once: the pages are prerendered at build time, where
 * there is no viewer whose locale we could read. Prerendered HTML is therefore DEFAULT_LOCALE,
 * and `initLocale()` runs on mount in the root layout to switch to the viewer's actual locale.
 * Because `localeTag` is `$state`, that switch re-renders every component holding a `$derived`
 * translator — which is why components must derive rather than capture:
 *
 *   const t = $derived(getTranslations(messages));   // re-renders when the locale lands
 *   const t = getTranslations(messages);             // frozen in English forever
 *
 * The locale comes from the browser only; there is no user-facing language picker yet.
 */
import {
  DEFAULT_LOCALE,
  detectLocaleTag,
  fallbackCurrencyCode,
  makeFormatters,
  makeTranslator,
  mergeMessages,
} from "$i18n";
import common from "$lib/messages/common.js";

let localeTag = $state(DEFAULT_LOCALE);

/** Adopt the viewer's locale. Call once, on mount, from the root layout. */
export function initLocale() {
  localeTag = detectLocaleTag();
}

/** The active locale tag, e.g. "es-MX". */
export function getLocaleTag() {
  return localeTag;
}

/**
 * A `t(key, values, opts)` for one feature's messages, with common as the fallback base.
 *
 * @param {Record<string, any>} [messages] a `$lib/messages/<feature>.js` module; omit for
 *   shared components, which only use common strings
 */
export function getTranslations(messages) {
  return makeTranslator(mergeMessages(localeTag, messages, common), localeTag);
}

/**
 * Intl formatters for the active locale.
 *
 * @param {string} [currencyCode] defaults to the locale region's currency
 */
export function getFormatters(currencyCode) {
  const region = new Intl.Locale(localeTag).region;
  return makeFormatters(localeTag, currencyCode ?? fallbackCurrencyCode(region));
}
